/* eslint-disable @typescript-eslint/no-unused-vars */
import Close from '@Bright/images/Close.svg';
import Rupee from '@common/components/Rupee';
import SelectVariants, { useVariant } from '@common/components/SelectVariants';
import Savings from '@common/components/Tags/Savings';
import { ReactComponent as VariantCircleRemove } from '@common/icons/VariantCircleRemove.svg';
import Product, { computeProductLink, PRODUCT_REFERRED_FROM } from '@common/models/Product';
import { RootState } from '@common/store';
import { addProductToCart, subtractProductFromCart } from '@common/store/cart/slice';
import { FeaturesSliceType } from '@common/store/features/slice';
import {
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  ProductDetailsSliceType,
} from '@common/store/productDetails/slice';
import { StoreInfoSliceType } from '@common/store/storeInfo/slice';
import { addToast, removeToast, ToastWithKeyType } from '@common/store/toasts/slice';
import { getMerchantJWTToken } from '@common/utils/token';
import Event from '@components/Event';
import QuickViewProduct from '@components/Product';
import ProductCardOverlay from '@components/ProductCardOverlay';
import Toasts from '@components/Toasts';
import Button from '@lib/Button';
import Image from '@lib/Image';
import Link from '@lib/Link';
import { LAYOUT_CONFIG } from '@theme/utils/constants';
import classnames from 'classnames';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { EmptyProductCard } from '../../../../common/components/EmptyProductCard';
import Modal from './Modal';

export interface ProductCardProps {
  addProductToCart: typeof addProductToCart;
  subtractProductFromCart: typeof subtractProductFromCart;
  product: Product;
  cartProductQuantity: number;
  addToast: typeof addToast;
  referredFrom?: PRODUCT_REFERRED_FROM;
  fetchProductDetailsRequest: typeof fetchProductDetailsRequest;
  fetchProductReviewsRequest: typeof fetchProductReviewsRequest;
  productDetails: ProductDetailsSliceType;
  toasts: ToastWithKeyType[];
  removeToast: typeof removeToast;
  storeInfo: StoreInfoSliceType;
  features?: FeaturesSliceType;
}

const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  product,
  addProductToCart,
  referredFrom,
  toasts,
  removeToast,
  cartProductQuantity,
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  productDetails,
  storeInfo,
  features,
}) => {
  if (!product) {
    return <EmptyProductCard dimensionW={'w-248'} />;
  }

  const [isVariantOverlayOpen, setIsVariantOverlayOpen] = useState(false);
  const [CloseDetails, SetCloseDetails] = useState(false);
  const computedProductLink = computeProductLink({ product, referredFrom });
  const { selectedOptions, setSelectedOptions, quantity, setQuantity, selectedVariant } = useVariant(product);

  return (
    <div className="flex flex-col rounded-md bg-white h-full w-248 hover:shadow-productCardCard border border-border border-opacity-75 hover:border-0">
      {CloseDetails && productDetails.productDetails ? (
        <Modal
          heading=""
          onClose={() => {
            document.body.style.overflow = 'scroll';
            SetCloseDetails(!CloseDetails);
          }}
          closeModal={() => {
            document.body.style.overflow = 'scroll';
            SetCloseDetails(false);
          }}
          className={classnames(
            'zoomInFull modal-open QuickView w-full z-999 md:mx-0 md:mb-100 rounded-2 md:rounded-4',
            {
              'mt-40 md:mt-0': !getMerchantJWTToken(),
              'mt-120 md:mt-0': getMerchantJWTToken(),
            },
          )}
        >
          <div className="relative">
            {toasts.length ? (
              <div className="w-369 max-w-410 hidden md:block max-auto" id="QuickViewToasts">
                <Toasts toasts={toasts} addToast={addToast} removeToast={removeToast} quickView={true} />
              </div>
            ) : null}
            <div className="flex justify-between mb-30 md:mb-16 items-center">
              <Image
                src={Close}
                alt="Close"
                className="float-right absolute hidden md:block right-0 mt-10 cursor-pointer h-12 w-12 md:h-14 md:w-14"
                onClick={() => {
                  document.body.style.overflow = 'scroll';
                  SetCloseDetails(!CloseDetails);
                }}
                noDefaultDimensions
                noDefaultFit
              />
            </div>
            <div className="overflow-x-auto hideScroll">
              {productDetails.productDetails.isEvent ? (
                <Event
                  subtractProductFromCart={subtractProductFromCart}
                  product={productDetails.productDetails}
                  addProductToCart={addProductToCart}
                  addToast={addToast}
                  cartProductQuantity={cartProductQuantity}
                  isZoomEnabled={false}
                  storeType={storeInfo.storeInfo?.storeType}
                  quickView={true}
                  review={productDetails?.reviews?.length}
                  productVideoFeature={features.features?.productVideo}
                />
              ) : (
                <QuickViewProduct
                  subtractProductFromCart={subtractProductFromCart}
                  product={productDetails.productDetails}
                  addProductToCart={addProductToCart}
                  addToast={addToast}
                  cartProductQuantity={cartProductQuantity}
                  isZoomEnabled={false}
                  storeType={storeInfo.storeInfo?.storeType}
                  review={productDetails.reviews?.length}
                  quickView={true}
                  productVideoFeature={features.features?.productVideo}
                />
              )}
            </div>
            <div className="md:hidden pt-24"></div>
          </div>
        </Modal>
      ) : null}

      <div className="h-248 relative overflow-hidden">
        <Link to={computeProductLink({ product, referredFrom })}>
          <Image src={product.images[0]} alt={product.title} className={'rounded-t-sm'} />
        </Link>

        <ProductCardOverlay
          product={product}
          fetchProductDetailsRequest={fetchProductDetailsRequest}
          fetchProductReviewsRequest={fetchProductReviewsRequest}
          removeToast={removeToast}
          toasts={toasts}
          CloseDetails={CloseDetails}
          SetCloseDetails={SetCloseDetails}
          computedProductLink={computedProductLink}
        />
      </div>
      <Link to={computeProductLink({ product, referredFrom })} className="p-12 flex flex-col flex-1">
        <div className="flex-grow">
          <div
            className="text-productCardTitleSm md:text-productCardTitleMd text-black mt-8 mb-8 break-words line-clamp-2"
            font-customisation="section-heading"
            color-customisation="page-heading"
          >
            {product.title}
          </div>
        </div>
        <div>
          <div className="flex flex-wrap justify-between items-center">
            <div className="" font-customisation="para-text">
              <div className="flex items-center">
                <div
                  className="text-theme font-medium text-productCardPriceSm md:text-productCardPriceMd mr-14"
                  color-customisation="page-body-text"
                >
                  <Rupee value={product.finalPrice} removeSpaceAfterRupee />
                </div>
                {product.hasDiscount ? (
                  <div className="line-through font-normal text-productCardDiscountPriceSm md:text-productCardDiscountPriceMd">
                    <Rupee value={product.price} removeSpaceAfterRupee />
                  </div>
                ) : (
                  <div className="font-normal">&nbsp;</div>
                )}
              </div>
            </div>
            {LAYOUT_CONFIG?.productList?.showDiscountLabel !== false ? (
              <div className="ml-auto">
                {product.savings ? <Savings className="p-2" percentage={product.savings.percentage} /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </Link>
      {isVariantOverlayOpen ? (
        <div
          className="absolute cursor-default top-0 left-0 bg-white z-10 h-full w-full bg-opacity-95 p-16 flex flex-col overflow-y-scroll"
          onClick={(e) => e.preventDefault()}
        >
          <div className="flex-grow">
            <VariantCircleRemove onClick={() => setIsVariantOverlayOpen(false)} className="ml-auto cursor-pointer" />
          </div>
          <SelectVariants
            product={product}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            quantity={quantity}
            setQuantity={setQuantity}
          />
          <div className="flex flex-wrap justify-between mt-16">
            <Button
              type="addToCart"
              className="text-white bg-theme mt-8 flex-1 min-w-150"
              onClick={(e) => {
                e.preventDefault();
                addProductToCart({
                  product: selectedVariant,
                  quantity,
                  disableSuccessToasts: true,
                  mixpanelProperties: {
                    source: 'theme-listing',
                  },
                });
                setSelectedOptions(product.optionValues.map(() => 0));
                setQuantity(1);
              }}
            />
            <Button
              className="hover:border-theme text-theme flex-1 min-w-100 mt-8"
              onClick={(e) => {
                e.preventDefault();
                setIsVariantOverlayOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default connect(
  ({ productDetails, toasts, features }: RootState) => ({
    productDetails,
    toasts,
    features,
  }),
  {
    fetchProductDetailsRequest,
    fetchProductReviewsRequest,
    removeToast,
  },
)(ProductCard);
